import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Price } from '@/components/Price/Price';
import styles from './TipChip.module.scss';

interface Props {
  selected: boolean;
  onClick: () => void;
  amount: number;
  disabled: boolean;
  percentage: number;
  isCustom?: boolean;
  mostCommonPercentage: number;
}

const TipChip = ({
  selected,
  disabled,
  onClick,
  amount,
  percentage,
  isCustom,
  mostCommonPercentage,
}: Props) => {
  const { t } = useTranslation();

  const renderTipContent = () => {
    if (isCustom) {
      return (
        <>
          <div className={styles.percentage}>{t('checkout.tipChip.custom')}</div>
          <div
            className={clsx(styles.amount, { [styles.hide]: amount === 0 })}
            data-testid={`tip-custom-amount`}
          >
            <Price amount={amount} />
          </div>
        </>
      );
    }

    if (percentage !== 0) {
      return (
        <>
          <div className={styles.percentage} data-testid={`tip-${percentage}`}>
            {t('checkout.tipChip.percentage', { percentage })}
          </div>
          <div className={styles.amount} data-testid={`tip-${percentage}-amount`}>
            <Price amount={amount} />
          </div>
        </>
      );
    }

    // Split "No, Thanks" into two lines using split
    const noThanksText = t('checkout.tipChip.noThanks');
    const [firstLine, ...rest] = noThanksText.split(',');
    const secondLine = rest.join(',').trim();

    return (
      <div className={styles.percentage}>
        {firstLine},
        <br /> {secondLine}
      </div>
    );
  };

  return (
    <div>
      {percentage !== 0 && percentage === mostCommonPercentage && (
        <div className={styles.tipLabel}>{t('checkout.tipChip.mostCommon')}</div>
      )}
      <div
        className={clsx(styles.root, {
          [styles.selected]: selected,
          [styles.disabled]: disabled,
        })}
        data-selected={selected.toString()}
        data-testid={isCustom ? 'tip-chip-custom' : `tip-chip-${percentage}`}
        onClick={onClick}
      >
        {renderTipContent()}
      </div>
    </div>
  );
};

export default TipChip;
